import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, CheckCircle2, Phone, MapPin } from "lucide-react";
import { ZONES, PHONE_TEL } from "@/lib/site";
import Reveal, { SectionHead, EASE } from "@/components/Reveal";

const normalize = (s) => s.toLowerCase().trim();

function findZone(query) {
  const q = normalize(query);
  if (q.length < 2) return null;
  return (
    ZONES.find((z) => normalize(z.name).includes(q)) ||
    ZONES.find((z) => z.areas.some((a) => normalize(a).includes(q))) ||
    null
  );
}

export default function Coverage() {
  const [query, setQuery] = useState("");
  const result = findZone(query);

  return (
    <section id="copertura" className="py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
        <div>
          <SectionHead
            kicker="Dove interveniamo"
            title="Copertura totale: Roma e Provincia"
            sub="Un'unità mobile sempre in movimento nella tua zona. Controlla il tuo quartiere in un secondo."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {ZONES.map((zone) => (
                <button
                  key={zone.name}
                  type="button"
                  onClick={() => setQuery(zone.name)}
                  className="rounded-full border border-line bg-card px-4 py-2 text-xs font-medium text-slate-300 transition-all duration-300 hover:border-volt/50 hover:text-volt"
                >
                  {zone.name}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <div className="rounded-3xl border border-line bg-card p-7 sm:p-9">
            <label
              htmlFor="zone-search"
              className="font-mono text-xs uppercase tracking-[0.25em] text-black"
            >
              Verifica la tua zona
            </label>
            <div className="relative mt-4">
              <Search size={19} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                id="zone-search"
                data-testid="coverage-zone-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Es. Trastevere, EUR, Frascati…"
                className="h-14 w-full rounded-full border border-line bg-ink pl-13 pr-5 text-base text-white placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-volt/60 focus:shadow-[0_0_0_3px_rgba(255,195,0,0.15)]"
                style={{ paddingLeft: 52 }}
              />
            </div>

            <AnimatePresence mode="wait">
              {result ? (
                <motion.div
                  key={result.name}
                  data-testid="coverage-zone-result"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-5"
                >
                  <p className="flex items-center gap-2.5 font-display text-base font-bold text-emerald-400">
                    <CheckCircle2 size={19} strokeWidth={2.3} />
                    Siamo operativi: {result.name}
                  </p>
                  <p className="mt-2 text-sm text-slate-400">
                    {result.areas.slice(0, 6).join(" · ")}
                  </p>
                </motion.div>
              ) : query.length >= 2 ? (
                <motion.div
                  key="fallback"
                  data-testid="coverage-zone-result"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="mt-6 rounded-2xl border border-volt/30 bg-volt/5 p-5"
                >
                  <p className="font-display text-base font-bold text-volt">
                    Zona non in elenco? Chiamaci comunque.
                  </p>
                  <p className="mt-2 text-sm text-slate-400">
                    Copriamo tutta Roma e Provincia: verifica al telefono in 30 secondi.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    <a
                      href={PHONE_TEL}
                      data-testid="coverage-fallback-call"
                      className="inline-flex items-center gap-2 rounded-full bg-volt px-5 py-2.5 font-display text-sm font-bold text-night transition-all duration-300 hover:bg-volthi"
                    >
                      <Phone size={15} strokeWidth={2.5} /> Chiama ora
                    </a>
                  </div>
                </motion.div>
              ) : (
                <motion.p
                  key="idle"
                  data-testid="coverage-zone-result"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-6 flex items-center gap-2.5 text-sm text-slate-500"
                >
                  <MapPin size={16} className="text-volt" />
                  Scrivi il tuo quartiere o comune per confermare la copertura.
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
